import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Star, Hash, Camera, ShoppingBag, Phone, MessageCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';
import CSRFToken from '../security/CSRFToken';
import { validateEmail } from '../security/sanitize';
import { useAuth } from '../context/AuthContext';
import { useFeedback } from '../contexts/FeedbackContext';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const { resetPassword } = useAuth();
  const { handleAsyncOperation } = useFeedback();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Get CSRF token from form
    const formData = new FormData(e.target);
    const csrfToken = formData.get('csrfToken');
    
    // Validate CSRF token
    if (!csrfToken || csrfToken !== sessionStorage.getItem('csrfToken')) {
      setError('Security validation failed. Please refresh the page and try again.');
      return;
    }
    
    // Validate email
    const sanitizedEmail = validateEmail(email);
    
    if (!sanitizedEmail) {
      setError('Please enter a valid email address');
      return;
    }
    
    setMessage('');
    setError('');
    
    const operation = async () => {
      await resetPassword(sanitizedEmail);
      return { success: true };
    };

    try {
      await handleAsyncOperation(
        operation,
        {
          title: 'Sending Reset Email...',
          message: 'Please wait while we send the password reset link to your email.'
        },
        {
          title: 'Email Sent!',
          message: 'Password reset email sent successfully. Please check your inbox.',
          autoCloseDelay: 3000
        },
        {
          title: 'Failed to Send',
          message: 'Unable to send password reset email. Please try again.'
        }
      );
      setMessage('Password reset email sent! Please check your inbox.');
    } catch (error) {
      console.error('Password reset error:', error);
      setError('Failed to send password reset email. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50 relative overflow-hidden">
      <Header />
      
      {/* Background effects */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {/* Animated circles */}
        <motion.div 
          className="absolute rounded-full bg-primary/5 w-96 h-96 top-20 -left-48"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ 
            duration: 8, 
            repeat: Infinity,
            ease: "easeInOut" 
          }}
        />
        <motion.div 
          className="absolute rounded-full bg-blue-200/10 w-96 h-96 top-1/3 -right-20"
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{ 
            duration: 6, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
        <motion.div 
          className="absolute rounded-full bg-primary/5 w-64 h-64 bottom-20 left-1/4"
          animate={{ 
            scale: [1, 1.3, 1],
            opacity: [0.2, 0.3, 0.2],
          }}
          transition={{ 
            duration: 10, 
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2
          }}
        />
        
        {/* Grid pattern */}
        <div className="absolute inset-0 bg-grid-pattern opacity-15"></div>
        
        {/* Floating icons */}
        {[Star, Hash, Camera, ShoppingBag, Phone, MessageCircle].map((Icon, i) => (
          <motion.div
            key={`forgot-password-icon-${i}`}
            className="absolute text-primary opacity-25"
            style={{
              left: `${Math.random() * 90 + 5}%`,
              top: `${Math.random() * 80 + 10}%`,
            }}
            animate={{
              y: [0, Math.random() * 30 - 15],
              rotate: [0, Math.random() * 20 - 10]
            }}
            transition={{
              duration: Math.random() * 8 + 7,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          >
            <Icon className="w-8 h-8" />
          </motion.div>
        ))}
      </div>
      
      <div className="flex flex-col justify-center py-8 sm:px-6 lg:px-8 relative z-10 mt-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="text-center text-3xl font-extrabold text-gray-900">
            Reset your password
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Enter your email address and we'll send you instructions to reset your password.
          </p>
        </div>

        <div className="mt-6 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            {error && (
              <div className="rounded-md bg-red-50 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">{error}</h3>
                  </div>
                </div>
              </div>
            )}
            
            {message && (
              <div className="rounded-md bg-green-50 p-4 mb-4">
                <div className="flex">
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-green-800">{message}</h3>
                  </div>
                </div>
              </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit}>
              <CSRFToken />
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Send reset instructions
                </button>
              </div>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or</span>
                </div>
              </div>

              <div className="mt-6 flex justify-center">
                <Link to="/login" className="font-medium text-primary hover:text-primary-dark">
                  Return to login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ForgotPassword;
